/**
 * parseVoiceCommand
 *
 * Parses a transcript spoken by the user into a typed command for the
 * Daily Entry grid. The grammar is intentionally small:
 *
 *   "stop" / "stop listening"        -> { type: 'stop' }
 *   "save" / "save now"              -> { type: 'save' }
 *   "clear" / "delete" / "erase"     -> { type: 'clear' }
 *   "next" / "next game"             -> { type: 'next' }
 *   "five" / "ten" / "twenty" / ...  -> { type: 'jump', denom: N }   (denominations only)
 *   "two seven nine" / "279" / etc.  -> { type: 'digits', digits: '279' }
 *   anything else                    -> { type: 'unknown', raw }
 *
 * Notes:
 *  - A single spoken digit-word ("five", "ten") is treated as a denom jump
 *    when it matches an available denomination, because running numbers are
 *    typically 2-3 digits — a bare digit isn't a useful number.
 *  - Multi-word digit phrases ("zero five" -> "05") are treated as digits.
 */
const NUMBER_WORDS = {
  zero: "0", oh: "0", o: "0",
  one: "1", won: "1",
  two: "2", too: "2", to: "2",
  three: "3", tree: "3",
  four: "4", for: "4", fore: "4",
  five: "5",
  six: "6", sicks: "6",
  seven: "7",
  eight: "8", ate: "8",
  nine: "9", niner: "9",
};

const DENOM_WORDS = {
  one: 1,
  two: 2,
  three: 3,
  five: 5,
  ten: 10,
  twenty: 20,
  thirty: 30,
  fifty: 50,
};

export function parseVoiceCommand(raw, opts = {}) {
  const denominations = opts.denominations || [1, 2, 3, 5, 10, 20, 30, 50];
  if (!raw) return { type: "noop" };

  const txt = String(raw).toLowerCase().replace(/[.,!?]/g, "").trim();
  if (!txt) return { type: "noop" };

  // Commands first.
  if (txt === "stop" || /\bstop (listening|dictation)\b/.test(txt)) {
    return { type: "stop" };
  }
  if (txt === "save" || /\bsave (now|it|this|page)\b/.test(txt)) {
    return { type: "save" };
  }
  if (txt === "clear" || txt === "delete" || txt === "erase" || /\bclear (cell|this)\b/.test(txt)) {
    return { type: "clear" };
  }
  if (txt === "next" || /\bnext (game|block|denom|denomination)\b/.test(txt)) {
    return { type: "next" };
  }

  const tokens = txt.split(/\s+/);

  // Single-token: prefer denomination jump over digit interpretation.
  if (tokens.length === 1) {
    const t = tokens[0];
    if (DENOM_WORDS[t] != null && denominations.includes(DENOM_WORDS[t])) {
      return { type: "jump", denom: DENOM_WORDS[t] };
    }
    // Direct numeric like "20" or "5" → also a jump if it matches a denom.
    if (/^\d+$/.test(t)) {
      const n = parseInt(t, 10);
      if (denominations.includes(n) && (n >= 10 || tokens[0].length >= 2)) {
        // "5" alone is ambiguous; only jump if user spoke 2+ digits like "10" "20"
        return { type: "jump", denom: n };
      }
      return { type: "digits", digits: t };
    }
    if (NUMBER_WORDS[t] != null) {
      return { type: "digits", digits: NUMBER_WORDS[t] };
    }
    return { type: "unknown", raw: txt };
  }

  // Multi-token: collect digit-equivalents.
  const digits = [];
  for (const tok of tokens) {
    if (/^\d+$/.test(tok)) { digits.push(tok); continue; }
    if (NUMBER_WORDS[tok] != null) { digits.push(NUMBER_WORDS[tok]); continue; }
    // ignore filler words
  }
  if (digits.length > 0) {
    return { type: "digits", digits: digits.join("") };
  }
  return { type: "unknown", raw: txt };
}
